import { Button } from "antd";
import type { PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useEffect, useRef, useState } from "react";
import { BaseItem as ZhBaseItem, Dialog as ZhDialog, Grid as ZhGrid, ZhMap, type ZhMapRef } from "zhihao-ui-react";

import { DEFAULT_VALUE } from "@/config";
import { PORT_TYPE } from "@zhihaoscm/meta";

// 位置查看只需要稳定的矢量底图和详情级缩放，不启用地图编辑能力。
const BERTH_MAP_ZOOM = 13;

/** 将 Vue latLon 字符串转换为 zhihao 地图所需的经纬度中心点。 */
function parseBerthMapCenter(latLon?: string): [number, number] | undefined {
    const coordinates = latLon?.split(",").map(value => Number(value.trim()));
    const longitude = coordinates?.[0];
    const latitude = coordinates?.[1];
    return longitude !== undefined && latitude !== undefined && Number.isFinite(longitude) && Number.isFinite(latitude)
        ? [longitude, latitude]
        : undefined;
}

/** 将 Vue displayArea 的坐标数组转换为 zhihao 地图业务图层坐标。 */
function parseDisplayArea(displayArea?: string[]): number[][] {
    return (displayArea || []).flatMap(point => {
        const coordinates = point?.split(",").map(value => Number(value.trim()));
        return coordinates?.length === 2 && coordinates.every(value => Number.isFinite(value)) ? [coordinates] : [];
    });
}

export interface BerthLocationModalProps {
    berth: Pick<
        PortWharfBerthVo,
        "id" | "name" | "shortName" | "address" | "fullAddress" | "latLon" | "displayArea"
    > & { type?: PortWharfBerthVo["type"] };
    disabled?: boolean;
}

/** 以 React 弹窗承载 Vue MapDialog 的只读位置数据，并提供坐标地图查看入口。 */
export function BerthLocationModal({ berth, disabled = false }: BerthLocationModalProps) {
    const [open, setOpen] = useState(false);
    const mapRef = useRef<ZhMapRef | null>(null);
    const hasCoordinate = Boolean(berth.latLon?.trim());
    const mapCenter = parseBerthMapCenter(berth.latLon);
    const mapUrl = hasCoordinate ? `https://uri.amap.com/marker?position=${encodeURIComponent(berth.latLon)}` : "#";

    // 地图挂载后恢复 Vue MapDialog 的点、线、面显示规则，避免 displayArea 只展示文本而不展示业务图层。
    useEffect(() => {
        if (!open) return;
        const coordinates = parseDisplayArea(berth.displayArea);
        const map = mapRef.current;
        if (!map) return;
        if (berth.type !== PORT_TYPE.HARBOR.code && coordinates.length > 0) {
            if (coordinates.length === 1) {
                map.wharf?.createWharfPoint({
                    id: String(berth.id),
                    name: berth.shortName || berth.name || "",
                    coordinate: coordinates[0],
                    type: 2
                });
            } else if (coordinates.length === 2) {
                map.wharf?.createWharfLine({
                    id: String(berth.id),
                    name: berth.shortName || berth.name || "",
                    coordinates,
                    type: 2
                });
            } else if (coordinates.length > 2) {
                map.wharf?.createWharfPolygon({
                    id: String(berth.id),
                    name: berth.shortName || berth.name || "",
                    coordinates,
                    type: 2
                });
            }
        } else if (mapCenter) {
            // 港口或缺少有效 displayArea 时沿用 Vue 的默认点标记，避免地图只有中心没有位置标识。
            map.position?.renderHtml(
                "",
                mapCenter,
                `<div class="custom-content-marker"><span>${berth.latLon || ""}</span></div>`,
                { offset: [-20, -60] }
            );
        }
        const center =
            berth.type === PORT_TYPE.HARBOR.code
                ? mapCenter
                : coordinates.length
                  ? coordinates[Math.floor(coordinates.length / 2)]
                  : mapCenter;
        if (center) map.setCenter(center[0], center[1]);
    }, [berth.displayArea, berth.id, berth.latLon, berth.name, berth.shortName, berth.type, mapCenter, open]);

    return (
        <>
            <Button type="link" disabled={disabled || !hasCoordinate} onClick={() => setOpen(true)}>
                位置
            </Button>
            <ZhDialog
                title={`查看位置${berth.shortName ? `-${berth.shortName}` : ""}`}
                open={open}
                footer={null}
                width="920px"
                closeOnClickModal={false}
                destroyOnClose
                style={{ minWidth: 450 }}
                onCancel={() => setOpen(false)}
            >
                {/* 位置字段沿用 Vue 的单列详情顺序，统一迁移到 zhihao 网格摘要组件。 */}
                <ZhGrid cols={1} gap={[20, 12]}>
                    <ZhBaseItem label="名称" value={berth.name || DEFAULT_VALUE} />
                    <ZhBaseItem label="位置" value={berth.fullAddress || berth.address || DEFAULT_VALUE} />
                    <ZhBaseItem label="经纬度" value={berth.latLon || DEFAULT_VALUE} />
                    <ZhBaseItem
                        label="显示区域"
                        value={berth.displayArea?.length ? berth.displayArea.join(" ; ") : DEFAULT_VALUE}
                    />
                </ZhGrid>
                <ZhMap
                    center={mapCenter}
                    className="mt-4 h-80 w-full"
                    layerType="vector"
                    ref={mapRef}
                    showScale
                    zoom={BERTH_MAP_ZOOM}
                />
                {hasCoordinate ? (
                    <Button className="mt-4" href={mapUrl} target="_blank" rel="noreferrer">
                        在地图中查看
                    </Button>
                ) : null}
            </ZhDialog>
        </>
    );
}
